import { useState } from 'react';
import { DEVISE, fmt } from '../../lib/currency';
import { localeDates, tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { dateIso } from './TicketsA4.jsx';

/**
 * Choisir des mois en les touchant : douze grosses cases, l'année avec ‹ ›.
 *
 * <p>« J'aurais préféré envoyer par date, où je peux sélectionner par mois :
 * propose-moi quelque chose de beaucoup plus simple. » Sous chaque mois, le
 * nombre de pièces et leur montant ; un mois sans pièce est grisé. Le même
 * choix partout (dépenses, tickets, factures de fournisseurs, et plus tard les
 * fiches de salaire) : il ne connaît que des dates et des montants.
 */

/** L'année la plus récente des pièces, sinon l'année en cours. */
export function anneeLaPlusRecente(elements, maintenant = new Date()) {
  const annees = (elements || []).map((x) => dateIso(x.date).slice(0, 4)).filter(Boolean);
  return annees.length ? annees.sort().reverse()[0] : String(maintenant.getFullYear());
}

/**
 * Les mois d'une année qui ont des pièces, « AAAA-MM » : le choix de départ.
 *
 * @param {object[]} elements pièces ({date, montant})
 * @param {string|number} annee l'année
 * @returns {string[]} les mois
 */
export function moisDeLAnnee(elements, annee) {
  return [
    ...new Set(
      (elements || [])
        .map((x) => dateIso(x.date).slice(0, 7))
        .filter((m) => m.startsWith(String(annee))),
    ),
  ].sort();
}

/**
 * @param {object} props propriétés
 * @param {object[]} props.elements pièces : `date` (« 10.10.2026 » ou ISO) et `montant`
 * @param {string[]} props.mois mois choisis, « AAAA-MM »
 * @param {(mois: string[]) => void} props.onChange nouveau choix
 * @param {string|number} [props.annee] année montrée au départ
 * @returns {import('react').ReactElement} les cases des mois
 */
export function ChoixDesMois({ elements, mois, onChange, annee: depart }) {
  const [annee, setAnnee] = useState(+(depart || anneeLaPlusRecente(elements)));
  const choisis = new Set(mois || []);
  const parMois = new Map();
  (elements || []).forEach((x) => {
    const m = dateIso(x.date).slice(0, 7);
    if (!m) return;
    const v = parMois.get(m) || { n: 0, total: 0 };
    v.n += 1;
    v.total += +x.montant || 0;
    parMois.set(m, v);
  });
  const annees = [...parMois.keys()].map((m) => +m.slice(0, 4));
  const min = Math.min(annee, ...annees);
  const max = Math.max(annee, new Date().getFullYear(), ...annees);
  const cle = (i) => annee + '-' + String(i + 1).padStart(2, '0');
  const bascule = (m) => {
    const s = new Set(choisis);
    if (s.has(m)) s.delete(m);
    else s.add(m);
    onChange([...s].sort());
  };
  const autresAnnees = [...choisis].filter((m) => !m.startsWith(String(annee)));
  const toutLAnnee = () => {
    const avec = moisDeLAnnee(elements, annee);
    const tous = avec.length ? avec : Array.from({ length: 12 }, (_, i) => cle(i));
    onChange([...new Set([...autresAnnees, ...tous])].sort());
  };
  const fleche = (sens, texte, nom, actif) => (
    <button
      type="button"
      aria-label={nom}
      disabled={!actif}
      onClick={() => setAnnee((a) => a + sens)}
      style={{
        width: 44,
        height: 44,
        borderRadius: 11,
        border: '1px solid ' + C.border,
        background: '#fff',
        fontSize: 20,
        fontWeight: 800,
        color: actif ? C.ink : C.muted,
        cursor: actif ? 'pointer' : 'default',
      }}
    >
      {texte}
    </button>
  );
  const petit = {
    flex: 1,
    border: '1px solid ' + C.border,
    background: '#fff',
    borderRadius: 10,
    padding: '9px 6px',
    fontSize: 11.5,
    fontWeight: 700,
    color: C.ink,
    cursor: 'pointer',
  };

  return (
    <div data-testid="choix-des-mois">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        {fleche(-1, '‹', tr('Année précédente'), annee > min)}
        <b style={{ fontSize: 18 }}>{annee}</b>
        {fleche(1, '›', tr('Année suivante'), annee < max)}
      </div>
      <div style={{ fontSize: 11.5, color: C.grey, marginBottom: 6 }}>
        {tr('Touche les mois que tu veux :')}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 6,
          marginBottom: 8,
        }}
      >
        {Array.from({ length: 12 }, (_, i) => {
          const m = cle(i);
          const v = parMois.get(m);
          const oui = choisis.has(m);
          const nom = new Date(annee, i, 1).toLocaleDateString(localeDates(), { month: 'short' });
          return (
            <button
              key={m}
              type="button"
              aria-pressed={oui}
              aria-label={new Date(annee, i, 1).toLocaleDateString(localeDates(), {
                month: 'long',
                year: 'numeric',
              })}
              onClick={() => bascule(m)}
              style={{
                border: oui ? 'none' : '1px solid ' + (v ? C.border : '#E5E7EB'),
                background: oui ? C.teal : v ? '#fff' : '#F9FAFB',
                color: oui ? '#fff' : v ? C.ink : C.muted,
                borderRadius: 11,
                padding: '8px 2px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <b style={{ fontSize: 13 }}>
                {oui ? '✓ ' : ''}
                {nom}
              </b>
              <span style={{ fontSize: 9.5, opacity: 0.9 }}>
                {v ? v.n + ' · ' + fmt(v.total) : '—'}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button type="button" onClick={toutLAnnee} style={petit}>
          {tr('Toute l’année')}
        </button>
        <button type="button" onClick={() => onChange(autresAnnees)} style={petit}>
          {tr('Effacer')}
        </button>
      </div>
    </div>
  );
}

/**
 * Le résultat du choix, en une ligne : « 4 mois · 8 pièces · CHF 31.60 ».
 *
 * @param {object} props propriétés
 * @param {string[]} props.mois mois choisis
 * @param {number} props.pieces nombre de pièces
 * @param {number} props.total montant
 * @returns {import('react').ReactElement} la ligne
 */
export function ResumeDesMois({ mois, pieces, total }) {
  return (
    <div
      data-testid="resume-des-mois"
      style={{
        background: '#fff',
        border: '1px solid ' + C.border,
        borderRadius: 11,
        padding: '10px 12px',
        fontSize: 13,
        fontWeight: 800,
        color: C.ink,
        marginBottom: 8,
      }}
    >
      {trf('{0} mois · {1} pièce(s)', (mois || []).length, pieces)} · {DEVISE} {fmt(total)}
    </div>
  );
}
